import React from "react";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend } from "recharts";
import { formatDistanceToNow } from "date-fns";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DASHBOARD_NAME } from "@/config/navigation";
import { PageHeader, KpiCard, Card, Funnel, SkeletonRows, EmptyState } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";

const RANGES = [["today", "Today"], ["7d", "7 Days"], ["30d", "30 Days"], ["90d", "90 Days"]];

export default function Overview() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [range, setRange] = React.useState("30d");
  const [kpis, setKpis] = React.useState(null);
  const [growth, setGrowth] = React.useState([]);
  const [funnel, setFunnel] = React.useState([]);
  const [dist, setDist] = React.useState([]);
  const [pending, setPending] = React.useState([]);
  const [activity, setActivity] = React.useState([]);
  const [perf, setPerf] = React.useState([]);

  React.useEffect(() => {
    api.get("/analytics/kpis", { params: { range } }).then(({ data }) => setKpis(data.cards)).catch(() => setKpis([]));
  }, [range]);

  React.useEffect(() => {
    const load = async () => {
      const calls = [
        api.get("/analytics/growth").then((r) => setGrowth(r.data.data)).catch(() => {}),
        api.get("/analytics/funnel").then((r) => setFunnel(r.data.stages)).catch(() => {}),
        api.get("/analytics/pending-actions").then((r) => setPending(r.data.items)).catch(() => {}),
        api.get("/activity-logs", { params: { page_size: 8 } }).then((r) => setActivity(r.data.items)).catch(() => {}),
      ];
      if (user.role === "super_admin") {
        calls.push(api.get("/analytics/distribution").then((r) => setDist(r.data.data)).catch(() => {}));
        calls.push(api.get("/analytics/partner-performance").then((r) => setPerf(r.data.data)).catch(() => {}));
      }
      await Promise.all(calls);
    };
    load();
  }, [user.role]);

  return (
    <div>
      <PageHeader title={DASHBOARD_NAME[user.role]} description={`Welcome back, ${user.name.split(" ")[0]}. Here's what's happening across your workspace.`}>
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
          {RANGES.map(([k, l]) => (
            <button key={k} onClick={() => setRange(k)} data-testid={`range-${k}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${range === k ? "bg-vs-royal text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
      </PageHeader>

      {/* KPIs */}
      {!kpis ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-6">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-28 rounded-xl bg-slate-100 animate-pulse" />)}</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-6">
          {kpis.map((c, i) => <KpiCard key={c.key} card={c} index={i} />)}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Card title="Business Growth" subtitle="Platform activity over the last 6 months" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={growth} margin={{ left: -20 }}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1D4ED8" stopOpacity={0.25} /><stop offset="95%" stopColor="#1D4ED8" stopOpacity={0} /></linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2} /><stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Area type="monotone" dataKey="requirements" stroke="#1D4ED8" fill="url(#g1)" strokeWidth={2} name="Requirements" />
              <Area type="monotone" dataKey="submissions" stroke="#8B5CF6" fill="url(#g2)" strokeWidth={2} name="Submissions" />
              <Area type="monotone" dataKey="quotations" stroke="#10b981" fillOpacity={0} strokeWidth={2} name="Quotations" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card title={user.role === "super_admin" ? "Requirement Funnel" : "Hiring Funnel"} subtitle="Pipeline conversion">
          {funnel.length ? <Funnel stages={funnel} /> : <SkeletonRows rows={6} />}
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Pending action center */}
        <Card title="Action Center" subtitle="What needs your attention" className="lg:col-span-1">
          <div className="space-y-2">
            {pending.length === 0 && <EmptyState icon="CheckCircle2" title="All clear" description="No pending actions right now." />}
            {pending.map((p) => (
              <button key={p.label} onClick={() => nav(`/app${p.link.replace("/admin", "")}`)} data-testid={`pending-${p.label.replace(/\s/g,'-').toLowerCase()}`}
                className="w-full flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5 hover:border-slate-200 hover:bg-slate-50 transition-colors">
                <span className="text-sm text-slate-700">{p.label}</span>
                <span className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${p.type === "danger" ? "text-rose-600" : p.type === "warning" ? "text-amber-600" : "text-vs-royal"}`}>{p.count}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
                </span>
              </button>
            ))}
          </div>
        </Card>

        {/* Distribution (admin) or performance placeholder */}
        {user.role === "super_admin" ? (
          <Card title="Partner Distribution" subtitle="Ecosystem composition">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={dist} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {dist.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        ) : (
          <Card title="Recent Activity" subtitle="Your latest workspace events">
            <ActivityFeed activity={activity} />
          </Card>
        )}

        {/* Activity feed (admin) or performance */}
        {user.role === "super_admin" ? (
          <Card title="Recent Activity" subtitle="Latest platform events">
            <ActivityFeed activity={activity} />
          </Card>
        ) : (
          <Card title="Monthly Activity" subtitle="Monthly submissions & quotations">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={growth} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                <Bar dataKey="submissions" fill="#1D4ED8" radius={[4, 4, 0, 0]} name="Submissions" />
                <Bar dataKey="quotations" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Quotations" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {user.role === "super_admin" && perf.length > 0 && (
        <Card title="Partner Performance" subtitle="Operational metrics across active partners" className="mt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="py-2.5 font-semibold">Partner</th><th className="py-2.5 font-semibold">Type</th>
                <th className="py-2.5 font-semibold">Activity</th><th className="py-2.5 font-semibold">Success</th><th className="py-2.5 font-semibold">Rating</th></tr></thead>
              <tbody>
                {perf.map((p) => (
                  <tr key={p.name} className="border-b border-slate-50">
                    <td className="py-2.5 font-medium text-slate-800">{p.name}</td>
                    <td className="py-2.5 capitalize text-slate-600">{p.type}</td>
                    <td className="py-2.5 text-slate-600">{p.activity}</td>
                    <td className="py-2.5 text-slate-600">{p.success}</td>
                    <td className="py-2.5"><span className="text-amber-500 font-semibold">★ {p.rating}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function ActivityFeed({ activity }) {
  if (!activity?.length) return <EmptyState icon="Activity" title="No activity yet" description="Actions will appear here." />;
  return (
    <div className="space-y-3">
      {activity.map((a) => (
        <div key={a.id} className="flex gap-3">
          <div className="mt-1 h-2 w-2 rounded-full bg-vs-royal shrink-0" />
          <div className="min-w-0"><div className="text-sm text-slate-700"><span className="font-medium">{a.actor_name}</span> {a.action}</div>
            <div className="text-xs text-slate-400">{a.timestamp ? formatDistanceToNow(new Date(a.timestamp), { addSuffix: true }) : ""}</div></div>
        </div>
      ))}
    </div>
  );
}
